import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * TypeSearch  —  universal type-and-search combobox (no dropdowns, no scroll pickers)
 *
 * Props:
 *   options       array of objects  — required when not using fetchFn
 *   fetchFn       async (query) => array  — for dynamic/async search
 *   value         string  — controlled display value (shown in input)
 *   onSelect      fn(item)  — called with full item on selection
 *   onClear       fn()  — called when field is cleared
 *   placeholder   string
 *   displayKey    string  — field to show after selection (fallback chain used if omitted)
 *   labelKeys     array   — fields to search/display in results (default: auto-detect)
 *   renderOption  fn(item, query) → ReactNode  — custom row renderer
 *   allowCreate   bool  — show "+ Create …" option
 *   onCreateNew   fn(query)
 *   disabled      bool
 *   minChars      number  — min chars before showing results (default 0 = show all on focus)
 *   maxResults    number  (default 50)
 */
export default function TypeSearch({
  options,
  fetchFn,
  value = '',
  onSelect,
  onClear,
  placeholder = 'Type to search…',
  displayKey,
  labelKeys,
  renderOption,
  allowCreate = false,
  onCreateNew,
  disabled = false,
  minChars = 0,
  maxResults = 50,
}) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync display value from parent
  useEffect(() => { setQuery(value || ''); }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const item = listRef.current.children[activeIndex];
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const getDisplay = useCallback((item) => {
    if (displayKey && item[displayKey]) return item[displayKey];
    return item.full_name || item.name || item.account_name || item.package_name || item.code || item.label || '';
  }, [displayKey]);

  const filterOptions = useCallback((q) => {
    if (!options) return [];
    const lower = q.toLowerCase();
    if (!lower) return options.slice(0, maxResults);
    return options.filter(o =>
      Object.values(o).some(v => v && String(v).toLowerCase().includes(lower))
    ).slice(0, maxResults);
  }, [options, maxResults]);

  const doSearch = useCallback(async (q) => {
    if (q.length < minChars && minChars > 0) { setResults([]); setOpen(false); return; }

    if (fetchFn) {
      setLoading(true);
      try {
        const r = await fetchFn(q);
        setResults(r || []);
        setOpen(true);
      } catch (e) {
        console.error('TypeSearch fetch error:', e);
      } finally {
        setLoading(false);
      }
      return;
    }

    const filtered = filterOptions(q);
    setResults(filtered);
    setOpen(true);
  }, [fetchFn, filterOptions, minChars]);

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    setActiveIndex(-1);
    if (!q && onClear) onClear();
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 150);
  };

  const handleFocus = () => {
    doSearch(query);
  };

  const handleSelect = (item) => {
    setQuery(getDisplay(item));
    setOpen(false);
    setResults([]);
    setActiveIndex(-1);
    onSelect(item);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setQuery('');
    setResults([]);
    setOpen(false);
    setActiveIndex(-1);
    if (onClear) onClear();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown') { e.preventDefault(); doSearch(query); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(results[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Tab') {
      if (activeIndex >= 0) { e.preventDefault(); handleSelect(results[activeIndex]); }
      else setOpen(false);
    }
  };

  const defaultRender = (item, q) => {
    const main = getDisplay(item);
    const sub = [
      item.client_code, item.supplier_code, item.account_code,
      item.package_code, item.email, item.supplier_type, item.phone,
    ].filter(Boolean).join(' · ');

    const highlight = (text) => {
      if (!q) return text;
      const idx = text.toLowerCase().indexOf(q.toLowerCase());
      if (idx === -1) return text;
      return <>{text.slice(0, idx)}<strong style={{ color: 'var(--primary)' }}>{text.slice(idx, idx + q.length)}</strong>{text.slice(idx + q.length)}</>;
    };

    return (
      <>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{highlight(main)}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{sub}</div>}
      </>
    );
  };

  const showDropdown = open && (results.length > 0 || allowCreate || (query.length >= minChars && !loading));

  return (
    <div style={{ position: 'relative' }} ref={wrapperRef}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          className="form-input"
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          style={{ paddingRight: query ? 30 : undefined }}
        />
        {query && !disabled && (
          <button onMouseDown={handleClear} tabIndex={-1} style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', color: 'var(--text-muted)',
            cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '0 2px',
          }}>×</button>
        )}
        {loading && (
          <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
            <div className="spinner" style={{ width: 12, height: 12 }} />
          </div>
        )}
      </div>

      {showDropdown && (
        <div ref={listRef} style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: 'white', border: '1px solid var(--border)', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto',
          marginTop: 2,
        }}>
          {results.map((item, i) => (
            <div
              key={item.id || i}
              onMouseDown={() => handleSelect(item)}
              style={{
                padding: '8px 12px', cursor: 'pointer',
                background: i === activeIndex ? 'rgba(79,110,247,0.08)' : 'white',
                borderBottom: i < results.length - 1 ? '1px solid var(--border)' : 'none',
              }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              {renderOption ? renderOption(item, query) : defaultRender(item, query)}
            </div>
          ))}

          {results.length === 0 && query && !loading && (
            <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              No results for "{query}"
            </div>
          )}

          {allowCreate && query && (
            <div
              onMouseDown={() => { setOpen(false); if (onCreateNew) onCreateNew(query); }}
              style={{ padding: '8px 12px', cursor: 'pointer', color: 'var(--primary)', fontWeight: 600, fontSize: 13, borderTop: results.length ? '1px solid var(--border)' : 'none' }}
            >
              + Create "{query}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}
